import { ElementType, PropsWithChildren } from 'react';
import { Box, BoxProps, useTheme } from '@mui/material';
import { FlexDirectionProperty } from 'csstype';

type StackProps<TElement extends ElementType> = BoxProps<TElement> & {
  /** Defines the `flex-direction` style property. It is applied for all screen sizes. */
  direction?: FlexDirectionProperty;
  /** Defines the space between immediate children. */
  spacing?: number;
  fullWidth?: boolean;
};

/**
 * Stack component. This component is used to stack components horizontally or vertically. It shares a partial API with [the MUI 5 Stack component](https://mui.com/material-ui/react-stack/), which is unavailable in MUI 4.
 *
 * NOTE: Our version does not (yet) support all of the MUI 5 component's features.
 *
 * @param props Stack props
 * @returns A stack of components
 * @deprecated Use the MUI 5 Stack component instead
 */
const Stack = <TElement extends ElementType = 'div'>({
  direction = 'column',
  justifyContent = 'start',
  spacing = 1,
  fullWidth,
  children,
  ...boxProps
}: PropsWithChildren<StackProps<TElement>>) => {
  const theme = useTheme();
  const gap = theme.spacing(spacing);
  const width = fullWidth ? '100%' : undefined;
  return (
    <Box
      display="flex"
      flexDirection={direction}
      justifyContent={justifyContent}
      style={{ gap, width }}
      flexWrap="wrap"
      {...boxProps}
    >
      {children}
    </Box>
  );
};

export type { StackProps };
export default Stack;
